<template>
  <b-modal
    v-model="showModal"
    :title="editId ? 'Edit Capital Invoice' : 'Add Capital Invoice'"
    size="lg"
    class="m-4"
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
    @ok="submitHandler"
  >
    <b-form
      @submit.prevent="submitHandler"
    >
      <b-row>
        <b-col cols="12">
          <b-alert
            variant="danger"
            :show="errorMessage ? true : false"
          >
            <div
              v-if="typeof errorMessage === 'string'"
              class="alert-body"
            >
              <p>
                {{ errorMessage }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.detail"
              class="alert-body"
            >
              <p>
                {{ errorMessage.detail }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.non_field_errors"
              class="alert-body"
            >
              <p>
                {{ errorMessage.non_field_errors[0] }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.message"
              class="alert-body"
            >
              <p>
                {{ errorMessage.message }}
              </p>
            </div>
          </b-alert>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label="Invoice Number"
            label-for="invoiceNo"
          >
            <b-form-input
              id="invoiceNo"
              v-model="invoiceNo"
              type="text"
              placeholder="Enter Invoice Number"
            />
            <p
              v-if="errorMessage && errorMessage.invoice_no"
              class="error-message"
            >
              {{ errorMessage.invoice_no[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label="Supplier"
          >
            <custom-v-select
              id="supplier"
              v-model="selectedSupplier"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="supplierOptions"
              label="text"
              placeholder="Select Supplier"
            />
            <p
              v-if="errorMessage && errorMessage.supplier"
              class="error-message"
            >
              {{ errorMessage.supplier[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <label for="invoice-date">Invoice Date</label>
          <b-form-datepicker
            id="invoice-date"
            v-model="invoiceDate"
            menu-class="w-100"
            calendar-width="100%"
          />
          <p
            v-if="errorMessage && errorMessage.date"
            class="error-message"
          >
            {{ errorMessage.date[0] }}
          </p>
        </b-col>

        <b-col cols="6">
          <label for="received-date">Received Date</label>
          <b-form-datepicker
            id="received-date"
            v-model="receivedDate"
            menu-class="w-100"
            calendar-width="100%"
          />
          <p
            v-if="errorMessage && errorMessage.recieved_date"
            class="error-message"
          >
            {{ errorMessage.recieved_date[0] }}
          </p>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label="Amount"
            label-for="amount"
          >
            <b-form-input
              id="amount"
              v-model="amount"
              type="number"
              placeholder="Enter Amount"
            />
            <p
              v-if="errorMessage && errorMessage.amount"
              class="error-message"
            >
              {{ errorMessage.amount[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label="Total Items"
            label-for="totalItems"
          >
            <b-form-input
              id="totalItems"
              v-model="totalItems"
              type="number"
              placeholder="Enter Total Items"
            />
            <p
              v-if="errorMessage && errorMessage.total_items"
              class="error-message"
            >
              {{ errorMessage.total_items[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            label="Remarks"
            label-for="remarks"
          >
            <b-form-textarea
              id="remarks"
              v-model="remarks"
              placeholder="Enter Remarks"
              rows="2"
            />
            <p
              v-if="errorMessage && errorMessage.remarks"
              class="error-message"
            >
              {{ errorMessage.remarks[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            label="Attach Files"
          >
            <b-form-file
              v-model="files"
              multiple
              placeholder="Choose files or drop them here..."
              drop-placeholder="Drop files here..."
            />
            <div
              v-if="existingFiles && existingFiles.length > 0"
              class="mt-1"
            >
              <small class="text-muted">Existing files:</small>
              <div class="d-flex flex-wrap mt-50">
                <div
                  v-for="(file, index) in existingFiles"
                  :key="index"
                  class="position-relative mr-3 border-primary rounded  px-1 d-inline-flex align-items-center"
                  :class="{ 'deleted-file': deleteFileIds.includes(file.id) }"
                >
                  <a
                    :href="file.file"
                    target="_blank"
                  >
                    <feather-icon
                      v-b-tooltip.hover="{boundary:'window'}"
                      title="View File"
                      icon="FileIcon"
                      size="20"
                      class="cursor-pointer mr-3 file-icon"
                    />
                    <span class="file-number">{{ index + 1 }}</span>
                  </a>
                  <feather-icon
                    v-b-tooltip.hover="{boundary:'window'}"
                    :title="deleteFileIds.includes(file.id) ? 'Undo Delete' : 'Delete File'"
                    :icon="deleteFileIds.includes(file.id) ? 'RotateCcwIcon' : 'TrashIcon'"
                    size="16"
                    class="cursor-pointer delete-icon ml-25"
                    :class="deleteFileIds.includes(file.id) ? 'text-warning' : 'text-danger'"
                    @click="toggleDeleteFile(file.id)"
                  />
                </div>
              </div>
            </div>
            <p
              v-if="errorMessage && errorMessage.files"
              class="error-message"
            >
              {{ errorMessage.files[0] }}
            </p>
          </b-form-group>
        </b-col>

      </b-row>
    </b-form>
    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="saving"
        @click="ok()"
      >
        {{ editId ? 'Update' : 'Create' }}
        <b-spinner
          v-if="saving"
          small
          label="Small Spinner"
        />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BButton, BFormInput, BFormDatepicker, BForm, BSpinner, BAlert, BFormFile, BFormTextarea, VBTooltip,
} from 'bootstrap-vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'AddCapitalGoodsInvoice',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    CustomVSelect,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BFormDatepicker,
    BButton,
    BSpinner,
    BAlert,
    BFormFile,
    BFormTextarea,
  },
  props: {
    editId: {
      type: [Number, String, null],
      default: null,
    },
  },
  data() {
    return {
      showModal: true,
      selectedSupplier: null,
      supplierOptions: [],
      invoiceNo: '',
      invoiceDate: null,
      receivedDate: null,
      amount: null,
      totalItems: null,
      remarks: '',
      files: [],
      existingFiles: [],
      deleteFileIds: [],
      saving: false,
      errorMessage: {},
    }
  },
  created() {
    this.fetchSupplierOptions()
    if (this.editId) {
      this.fetchInvoiceDetail()
    }
  },
  methods: {
    fetchSupplierOptions() {
      axios.get('/invoices/capital_supplier/', {
        params: {
          page: 1,
          per_page: 500,
        },
      })
        .then(res => {
          this.supplierOptions = (res.data.results || res.data).map(s => ({
            id: s.id,
            text: s.name,
          }))
        })
    },
    fetchInvoiceDetail() {
      axios.get(`/invoices/capital_invoice/${this.editId}/`)
        .then(res => {
          const { data } = res
          this.invoiceNo = data.invoice_no || ''
          this.invoiceDate = data.date || null
          this.receivedDate = data.recieved_date || null
          this.amount = data.amount || null
          this.totalItems = data.total_items || null
          this.remarks = data.remarks || ''
          this.existingFiles = data.files || []

          // Prepopulate supplier from supplier_detail
          if (data.supplier_detail) {
            const checkSupplier = () => {
              if (this.supplierOptions.length > 0) {
                this.selectedSupplier = this.supplierOptions.find(
                  s => s.id === data.supplier_detail.id,
                ) || null
              } else {
                setTimeout(checkSupplier, 200)
              }
            }
            checkSupplier()
          } else if (data.supplier) {
            const checkSupplier = () => {
              if (this.supplierOptions.length > 0) {
                this.selectedSupplier = this.supplierOptions.find(
                  s => s.id === data.supplier,
                ) || null
              } else {
                setTimeout(checkSupplier, 200)
              }
            }
            checkSupplier()
          }
        })
        .catch(error => {
          this.errorMessage = error?.response?.data || 'Failed to load invoice details'
        })
    },
    toggleDeleteFile(fileId) {
      const index = this.deleteFileIds.indexOf(fileId)
      if (index > -1) {
        this.deleteFileIds.splice(index, 1)
      } else {
        this.deleteFileIds.push(fileId)
      }
    },
    submitHandler(event) {
      event.preventDefault()
      const formData = new FormData()
      if (this.invoiceNo) {
        formData.append('invoice_no', this.invoiceNo)
      }
      if (this.invoiceDate) {
        formData.append('date', this.invoiceDate)
      }
      if (this.amount) {
        formData.append('amount', this.amount)
      }
      if (this.totalItems) {
        formData.append('total_items', this.totalItems)
      }
      if (this.receivedDate) {
        formData.append('recieved_date', this.receivedDate)
      }
      if (this.selectedSupplier) {
        formData.append('supplier', this.selectedSupplier.id)
      }
      formData.append('remarks', this.remarks || '')
      if (this.files && this.files.length > 0) {
        this.files.forEach(file => {
          formData.append('files', file)
        })
      }
      if (this.deleteFileIds.length > 0) {
        this.deleteFileIds.forEach(id => {
          formData.append('delete_files', id)
        })
      }

      this.saving = true

      const request = this.editId
        ? axios.patch(`/invoices/capital_invoice/${this.editId}/`, formData)
        : axios.post('/invoices/capital_invoice/', formData)

      request
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || (this.editId ? 'Invoice Updated' : 'Invoice Created'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.$emit('invoice-saved')
          this.saving = false
        })
        .catch(error => {
          this.errorMessage = error?.response?.data || 'Something went wrong'
          this.saving = false
        })
    },
  },
}
</script>

<style scoped>
.error-message {
  color: red
}

.file-number{
  position: absolute;
  top: 2px;
  left: 2px;
  color: black;
  /* border: 2px solid firebrick; */
}
.deleted-file {
  opacity: 0.4;
  text-decoration: line-through;
}
.delete-icon:hover {
  transform: scale(1.2);
}
</style>
